import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { toast } from "sonner";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { formatPrice } from "@/lib/format";

export default function Checkout() {
  const { items, subtotal } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name || "");
  const [address, setAddress] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (items.length === 0) {
      toast.error("Your bag is empty");
      return;
    }
    if (!name || !address) {
      toast.error("Provide shipping details");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        items: items.map((i) => ({ product_id: i.product_id, size: i.size, quantity: i.quantity })),
        origin_url: window.location.origin,
        shipping_name: name,
        shipping_address: address,
      };
      const { data } = await api.post("/checkout/session", payload);
      window.location.href = data.url;
    } catch (err) {
      toast.error(formatApiError(err));
      setSubmitting(false);
    }
  };

  if (items.length === 0) {
    return (
      <main className="max-w-3xl mx-auto px-6 py-32 text-center" data-testid="checkout-empty">
        <h1 className="font-display text-4xl">Your bag is empty.</h1>
        <Link to="/shop" className="btn-outline mt-10 inline-flex">Discover the Collection</Link>
      </main>
    );
  }

  return (
    <main className="max-w-6xl mx-auto px-6 sm:px-12 py-16" data-testid="checkout-page">
      <p className="tracking-overline text-[11px] uppercase opacity-60">Final Step</p>
      <h1 className="font-display text-5xl sm:text-6xl mt-3">Checkout</h1>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 mt-16">
        <form className="lg:col-span-7 space-y-8" onSubmit={handleCheckout}>
          <div>
            <h2 className="font-display text-2xl mb-2">Shipping Details</h2>
            <p className="text-sm opacity-60 mb-6">Your piece will be hand-packaged and dispatched by named courier.</p>
            <div className="space-y-6">
              <div>
                <label className="tm-label">Full Name</label>
                <input
                  className="tm-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="checkout-name-input"
                />
              </div>
              <div>
                <label className="tm-label">Shipping Address</label>
                <textarea
                  className="tm-input"
                  rows={3}
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  required
                  placeholder="Street, City, Postal Code, Country"
                  data-testid="checkout-address-input"
                />
              </div>
            </div>
          </div>

          {!user && (
            <div className="text-sm py-4 px-5" style={{ background: "var(--bone-muted)" }}>
              <Link to="/login" className="underline underline-offset-4" data-testid="checkout-login-link">Sign in</Link>
              {" "}to save your order history, or proceed as a guest.
            </div>
          )}

          <button
            type="submit"
            disabled={submitting}
            className="btn-primary w-full mt-6"
            data-testid="checkout-pay-button"
          >
            {submitting ? "Redirecting to Stripe..." : `Pay ${formatPrice(subtotal, items[0]?.currency)} Securely`}
          </button>
          <p className="text-xs opacity-60 text-center">Secured by Stripe. We do not store card details.</p>
        </form>

        <aside className="lg:col-span-5">
          <div className="border p-8" style={{ borderColor: "var(--line)", background: "var(--bone-muted)" }}>
            <p className="tracking-overline text-[11px] uppercase opacity-60 mb-6">Order Summary</p>
            <ul className="space-y-5" data-testid="checkout-items">
              {items.map((item) => (
                <li key={`${item.product_id}-${item.size}`} className="flex gap-4">
                  <div className="w-16 h-20 overflow-hidden flex-shrink-0" style={{ background: "var(--line-soft)" }}>
                    <img src={item.image} alt={item.name} className="object-cover w-full h-full" />
                  </div>
                  <div className="flex-1">
                    <p className="font-display text-base leading-tight">{item.name}</p>
                    <p className="text-[10px] uppercase tracking-[0.18em] opacity-60 mt-1">
                      Size {item.size} · Qty {item.quantity}
                    </p>
                  </div>
                  <p className="font-display text-base">{formatPrice(item.price * item.quantity, item.currency)}</p>
                </li>
              ))}
            </ul>
            <div className="mt-6 pt-6 space-y-2 border-t" style={{ borderColor: "var(--line)" }}>
              <div className="flex justify-between text-sm">
                <span className="opacity-70">Subtotal</span>
                <span>{formatPrice(subtotal, items[0]?.currency)}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="opacity-70">Shipping</span>
                <span className="italic">Calculated at next step</span>
              </div>
              <div className="flex justify-between font-display text-2xl pt-3 border-t mt-3" style={{ borderColor: "var(--line)" }}>
                <span>Total</span>
                <span data-testid="checkout-total">{formatPrice(subtotal, items[0]?.currency)}</span>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </main>
  );
}
